import React from "react";
import {
  emailArray,
  emailDefault,
  faxNumberDefault,
  isEmail,
  isPhoneNumberWithoutSpecialCharactersDefault,
  postalCodeDefault,
  requiredFieldDefault,
} from "../../../validationRules";
import addressFields from "./addressFields";
import _ from "lodash";
import legalForms from "../../../legalForms";
import { InputAdornment } from "@mui/material";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { overwriteFormElementField } from "../formDefinitionFunctions";

export const buildPaths = (path: string, translationPrefix: string) => {
  if (path !== "" && !_.endsWith(".", path)) {
    path = path + ".";
  }
  if (translationPrefix !== "" && !_.endsWith(".", translationPrefix)) {
    translationPrefix = translationPrefix + ".";
  }
  return {
    dataPath: path,
    translationPath: translationPrefix + path,
  };
};

export function contractualPartnerFields({
  path = "",
  translationPrefix = "",
  hideAddressAddition = true,
}: {
  path?: string;
  translationPrefix?: string;
  hideAddressAddition?: boolean;
}) {
  const { dataPath, translationPath } = buildPaths(path, translationPrefix);

  return [
    {
      type: "ValidatorTextField",
      path: `${dataPath}legalName`,
      translationPath: `${translationPath}legalName`,
      validators: requiredFieldDefault,
    },
    {
      type: "ValidatorTextField",
      path: `${dataPath}name`,
      translationPath: `${translationPath}name`,
      helperText: "Erleichtert die Suche z.B. “Mc Fit” bei der RSG Group oder “XXXLutz” bei der BDSK Handels GmbH.",
    },
    ...organizationBaseFields({ path, translationPrefix, omitAddress: true }),
    ...addressFields({
      path: `${dataPath}address`,
      translationPath: `${translationPath}address`,
      hidePostOfficeBoxFields: () => false,
      hideAddressAddition: hideAddressAddition,
    }),
    {
      type: "ValidatorSelect",
      path: `${dataPath}legalForm`,
      translationPath: `${translationPath}legalForm`,
      options: () => [
        { value: "__null__", label: "Keine Gesellschaftsform" },
        ..._.map(legalForms, (legalForm) => {
          return {
            value: legalForm.label,
            label: legalForm.label,
          };
        }),
      ],
    },
    {
      type: "ContractRepresentatives",
      path: `${dataPath}additionalRepresentatives`,
      translationPath: `${translationPath}additionalRepresentatives`,
    },
    {
      type: "OrganizationHintsForm",
      path: dataPath,
      translationPath: translationPath,
      additionalProps: {
        fullWidth: true,
        prependDivider: true,
      },
    },
  ];
}

// TODO: Temporary adjustment, will be removed & refactored later
export function debtCollectionAgencyFieldsReduced({
  ...params
}: Parameters<typeof debtCollectionAgencyFields>[0]) {
  const { dataPath } = buildPaths(params.path ?? "", params.translationPrefix ?? "");

  return debtCollectionAgencyFields(params).flatMap((field) => {
    if (field.path === `${dataPath}mainEmail.email` || field.path === `${dataPath}additionalEmails`) {
      return [];
    }

    return [field];
  });
}

export function debtCollectionAgencyFields({
  path = "",
  translationPrefix = "",
  omitAddress = false,
  requireAddressValidation = false,
}: {
  path?: string;
  translationPrefix?: string;
  omitAddress?: boolean;
  requireAddressValidation?: boolean;
}) {
  const { dataPath, translationPath } = buildPaths(path, translationPrefix);
  let baseFields = organizationBaseFields({
    path,
    translationPrefix,
    omitAddress,
    requireAddressValidation,
  });

  // TODO: Temporary adjustment, will be removed & refactored later
  baseFields = baseFields.flatMap((field) => {
    if (field.path === `${dataPath}mainEmail.email`) {
      return [
        {
          ...field,
        },
        {
          type: "AppendableListField",
          path: `${dataPath}additionalEmails`,
          translationPath: `${translationPath}additionalEmails`,
          additionalProps: {
            valueKey: "email",
            inputValidator: [
              {
                validator: isEmail,
                message: "Dies ist keine gültige E-Mail Adresse",
              },
            ],
          },
          validators: emailArray(),
          helperText:
            "Hier können bei Bedarf weitere E-Mail-Adressen von Ansprechpartnern oder Abteilungen hinterlegt werden. Bei Riverty z.B. paypal@riverty.com. Diese kann nach Speichern über “Fall E-Mail-Adresse” ausgewählt werden. ",
        },
      ];
    }

    return [field];
  });

  return [
    {
      type: "ValidatorTextField",
      path: `${dataPath}name`,
      translationPath: `${translationPath}name`,
      validators: requiredFieldDefault,
    },
    ...baseFields,
  ];
}

export function opposingLiabilityInsuranceFields({
  path = "",
  translationPrefix = "",
  omitAddress = false,
  requireAddressValidation = true,
}: {
  path?: string;
  translationPrefix?: string;
  omitAddress?: boolean;
  requireAddressValidation?: boolean;
}) {
  const { dataPath, translationPath } = buildPaths(path, translationPrefix);

  return [
    {
      type: "ValidatorTextField",
      path: `${dataPath}legalName`,
      translationPath: `${translationPath}legalName`,
      validators: requiredFieldDefault,
    },
    ...organizationBaseFields({ path, translationPrefix, omitAddress, requireAddressValidation }),
  ];
}

function organizationBaseFields({
  path = "",
  translationPrefix = "",
  omitAddress = false,
  requireAddressValidation = false,
}: {
  path?: string;
  translationPrefix?: string;
  omitAddress?: boolean;
  requireAddressValidation?: boolean;
}): FormElementPropsWithoutProduct[] {
  const { dataPath, translationPath } = buildPaths(path, translationPrefix);

  const getPreparedAddressFields = (): FormElementPropsWithoutProduct[] => {
    if (omitAddress) {
      return [];
    }

    const fields = addressFields({
      path: `${dataPath}address`,
      translationPath: `${translationPath}address`,
    });

    if (requireAddressValidation) {
      overwriteFormElementField(fields, `${dataPath}address.streetAddress`, { validators: requiredFieldDefault });
      overwriteFormElementField(fields, `${dataPath}address.postalCode`, {
        validators: [...requiredFieldDefault, postalCodeDefault],
      });
      overwriteFormElementField(fields, `${dataPath}address.addressLocality`, { validators: requiredFieldDefault });
    }

    return fields;
  };

  return [
    ...getPreparedAddressFields(),
    {
      type: "ValidatorTextField",
      path: `${dataPath}mainEmail.email`,
      translationPath: `${translationPath}mainEmail.email`,
      validators: emailDefault,
    },
    {
      type: "ValidatorTextField",
      path: `${dataPath}telephone`,
      translationPath: `${translationPath}telephone`,
      validators: isPhoneNumberWithoutSpecialCharactersDefault,
    },
    {
      type: "ValidatorTextField",
      path: `${dataPath}fax`,
      translationPath: `${translationPath}fax`,
      validators: faxNumberDefault,
      additionalProps: {
        InputProps: {
          startAdornment: <InputAdornment position="start">+49</InputAdornment>,
        },
      },
    },
  ];
}
